import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { chatActions } from 'store';
import { ChatHeader } from './chatHeader';
import { MessageList } from './messageList';
import { MessageInput } from './messageInput';
import { TypingIndicator } from './typingIndicator';
import { MESSAGE_PAGE_LIMIT } from './constants';
import { useChatSocket } from '../../utils/hooks/chatSocket';
import { FindLoad } from '../../utils';

const ACTION_TYPE = 'GET_MESSAGES';
export const ChatPanel = ({ currentUserId, currentUserType, onBack }) => {
    const dispatch = useDispatch();
    const activeChat = useSelector((state) => state.chat.activeChat);
    const messages = useSelector((state) => state.chat.messages);
    const { joinChat, leaveChat, startTyping, stopTyping } = useChatSocket();
    const prevMessageCountRef = useRef(0);
    const loader = FindLoad(ACTION_TYPE);

    useEffect(() => {
        if (!activeChat?.chatId) return;
        joinChat(activeChat.chatId);
        dispatch(chatActions.getMessages(activeChat.chatId, { skip: 0, limit: MESSAGE_PAGE_LIMIT }));
        dispatch(chatActions.markChatRead(activeChat.chatId));

        return () => {
            leaveChat(activeChat.chatId);
            dispatch(chatActions.clearMessages());
            prevMessageCountRef.current = 0;
        };
    }, [activeChat?.chatId]);

    // Mark chat as read when new messages arrive from other users while chat is open
    useEffect(() => {
        if (!activeChat?.chatId || !messages.length) return;
        // Skip the initial load — only trigger on subsequent new messages
        if (prevMessageCountRef.current > 0 && messages.length > prevMessageCountRef.current) {
            const newestMessage = messages[0];
            // Only mark as read if the new message is from someone else
            if (newestMessage?.sender?.userId !== currentUserId) {
                dispatch(chatActions.markChatRead(activeChat.chatId));
            }
        }
        prevMessageCountRef.current = messages.length;
    }, [messages.length, activeChat?.chatId, currentUserId]);

    // const handleDeleteChat = (chatId) => {
    //     dispatch(chatActions.deleteChat(chatId));
    //     dispatch(chatActions.clearActiveChat());
    //     // dispatch(chatActions.getChats('noLoad'));
    // };

    const handleDeleteMessage = (messageId) => {
        dispatch(chatActions.deleteMessage(messageId));
    };

    const handleStartTyping = () => {
        startTyping(activeChat?.chatId);
    };

    const handleStopTyping = () => {
        stopTyping(activeChat?.chatId);
    };

    if (!activeChat) return null;

    const ChatSkeleton = () => (
        <div className="chat-panel-skeleton">
            {[
                { id: 1, side: 'left' },
                { id: 2, side: 'left' },
                { id: 3, side: 'right' },
                { id: 4, side: 'left' },
                { id: 5, side: 'right' },
            ].map(({ id, side }) => (
                <div key={id} className={`chat-msg-skeleton chat-msg-skeleton-${side}`}>
                    {side === 'left' && <div className="chat-msg-skeleton-avatar" />}
                    <div className="chat-msg-skeleton-bubble">
                        <div className="chat-msg-skeleton-name" />
                        <div className="chat-msg-skeleton-text" />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="chat-panel-overlay">
            <ChatHeader
                chat={activeChat}
                currentUserId={currentUserId}
                onBack={onBack}
                // onDeleteChat={handleDeleteChat}
            />
            {loader?.length ? (
                <ChatSkeleton />
            ) : (
                <>
                    <MessageList
                        chatId={activeChat.chatId}
                        currentUserId={currentUserId}
                        currentUserType={currentUserType}
                        onDeleteMessage={handleDeleteMessage}
                    />
                    <TypingIndicator chatId={activeChat.chatId} participants={activeChat.participants} currentUserId={currentUserId} />
                </>
            )}
            <MessageInput
                chatId={activeChat.chatId}
                currentUserId={currentUserId}
                currentUserType={currentUserType}
                participants={activeChat.participants}
                onStartTyping={handleStartTyping}
                onStopTyping={handleStopTyping}
            />
        </div>
    );
};
